import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";

import { startEffectiveScriptRun } from "../../../lib/telephonyCampaignLoginApi";

/**
 * Loaded lazily, deliberately.
 *
 * ScriptPlay is ~3,300 lines and pulls the whole router-aware page
 * graph with it. A static import would put all of that into the
 * Production shell's own module graph -- and therefore into the module
 * graph of every test that renders the shell, none of which is about
 * running a Script. It also means the shell's bundle no longer carries
 * a page that is only reachable once a call connects.
 *
 * The component itself is unchanged and shared with the legacy
 * `/scripts/:id/play` route; only the moment it is loaded differs.
 */
const ScriptPlayPage = lazy(() => import("../../../pages/ScriptPlay"));

/**
 * AW-2B3.2 -- the Production Agent Workspace's Script panel.
 *
 * **Not a runner.** It owns no execution state, no node cursor, no
 * answers and no persistence: it obtains the authoritative run for the
 * current owned call and mounts the existing ScriptPlay runtime with
 * it. Everything a Script run does is still ScriptPlay's.
 *
 * **It invents no identity.** It sends no body on start -- no
 * `client_run_id`, no Script, no Lead -- because AW-2B3.1 made run
 * identity server-owned: the run is bound to the authoritative owned
 * call, so a refresh, a remount, a retry and two concurrent requests
 * all converge on the same run. A browser-minted token could only be
 * staler than the server's own binding.
 *
 * **Nothing is persisted client-side.** Run identity deliberately lives
 * in React state alone. Durable browser storage would create exactly the
 * stale-key hazard the server binding removes: a run id surviving across
 * a hangup could rebind the next call to the previous call's run. (The
 * feature's own static guard forbids durable storage here outright, and
 * that is deliberately not relaxed.)
 */

/**
 * The shell passes its own canonical `presenceState`, not the raw
 * backend phase -- it maps one onto the other in `applyCallSnapshot`
 * and never keeps the raw value. For the three call-bearing states the
 * two vocabularies are defined to the same strings ("connected",
 * "hanging_up", "wrap_up"), so comparing against CALL_PHASE is exact
 * rather than coincidental, and avoids either duplicating the shell's
 * enum or importing it back (which would be circular).
 *
 * Every other presence state -- READY, STARTING, ACTIVE, PAUSING,
 * PAUSED, ENDING, CONNECTING_AUDIO, AUDIO_FAILED, ACQUISITION_FAILED --
 * means there is no owned call, and falls through to the clear branch.
 */
export const WORKSPACE_RUN_PHASE = Object.freeze({
  CONNECTED: "connected",
  HANGING_UP: "hanging_up",
  WRAP_UP: "wrap_up",
});

/**
 * Written as literals rather than `CALL_PHASE.*` deliberately.
 *
 * Reading the enum at module scope means this module throws on import
 * in any test that mocks the telephony client without that export --
 * a module-level dependency on another module's shape, for three
 * string constants. `phaseVocabularyMatchesBackend()` below is exported
 * so a test can assert the equivalence directly, which is a stronger
 * guarantee than the dereference was: it fails loudly and specifically
 * if the backend vocabulary ever moves.
 */
export function phaseVocabularyMatchesBackend(callPhase) {
  return (
    callPhase.CONNECTED === WORKSPACE_RUN_PHASE.CONNECTED &&
    callPhase.HANGING_UP === WORKSPACE_RUN_PHASE.HANGING_UP &&
    callPhase.WRAP_UP === WORKSPACE_RUN_PHASE.WRAP_UP
  );
}

const PHASES_THAT_PRESERVE_A_RUN = Object.freeze([
  WORKSPACE_RUN_PHASE.CONNECTED,
  WORKSPACE_RUN_PHASE.HANGING_UP,
  WORKSPACE_RUN_PHASE.WRAP_UP,
]);

export function shouldStartRun(phase) {
  return phase === WORKSPACE_RUN_PHASE.CONNECTED;
}

export function shouldPreserveRun(phase) {
  return PHASES_THAT_PRESERVE_A_RUN.includes(phase);
}

export default function WorkspaceScriptRunner({ phase, onRunChange, customer = null }) {
  const [run, setRun] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | starting | ready | error
  // One start per owned call. The server would converge duplicates
  // anyway, but the shell polls call state every two seconds and there
  // is no reason to issue that request on every tick.
  const startedForPhaseRef = useRef(false);
  const cancelledRef = useRef(false);
  // AW-LDQ-003. Held in a ref so a caller that re-creates the callback
  // on every render cannot retrigger the phase effect -- which would
  // re-enter the run lifecycle for a reason that has nothing to do with
  // the call. The identity publication is a side channel; it must not
  // become an input to the state machine.
  const onRunChangeRef = useRef(onRunChange);
  onRunChangeRef.current = onRunChange;

  useEffect(() => () => {
    cancelledRef.current = true;
  }, []);

  const start = useCallback(async () => {
    setStatus("starting");
    try {
      const data = await startEffectiveScriptRun();
      const runId = data?.run_id;
      const scriptId = data?.script_id;
      // AW-2C-008. Optional on purpose: absent when no correlation
      // secret is configured, and the workspace then falls back to
      // fetching the customer after this response -- slower, equally
      // correct. Deliberately NOT part of the completeness check below:
      // a run without a correlation is still a run, and refusing to
      // mount one would turn a latency optimisation into an outage.
      const callCorrelationId = data?.call_correlation_id ?? null;
      if (!runId || !scriptId) {
        // A partial answer is not a run. Never mount ScriptPlay with
        // half an identity -- `embedded` is all-or-nothing by contract.
        throw new Error("aw2b3_incomplete_run_start_response");
      }
      if (cancelledRef.current) return;
      setRun({ runId, scriptId });
      setStatus("ready");
      // AW-LDQ-003. The Agent Tools need the SAME run identity this
      // component already owns, and must never resolve their own --
      // a second resolution path is a second lifecycle, and the two
      // would drift the moment one of them missed a phase change.
      // Publishing is the whole integration; nothing else is shared.
      if (typeof onRunChangeRef.current === "function") {
        // AW-2C-008. `callCorrelationId` travels with the run identity
        // because it names the same thing: the owned call this run
        // belongs to. It is opaque, inert, and compared for equality
        // only -- see `run_binding.owned_call_correlation`.
        onRunChangeRef.current({ runId, scriptId, callCorrelationId });
      }
    } catch (error) {
      if (cancelledRef.current) return;
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    if (shouldStartRun(phase)) {
      if (startedForPhaseRef.current) return;
      startedForPhaseRef.current = true;
      start();
      return;
    }

    if (shouldPreserveRun(phase)) {
      // HANGING_UP and WRAP_UP keep the mounted run: disposition and
      // notes happen in Wrap-up, and tearing the panel down there would
      // destroy work the agent is in the middle of.
      return;
    }

    // Any other phase -- IDLE, ACQUIRING, ACQUISITION_FAILED, ENDED --
    // means this work item is over or has not begun. Clear, so the next
    // CONNECTED starts cleanly and gets its own new run.
    startedForPhaseRef.current = false;
    setRun(null);
    setStatus("idle");
    // The run is over. Telling the tools immediately is what stops the
    // previous call's answers surviving into the next one.
    if (typeof onRunChangeRef.current === "function") onRunChangeRef.current(null);
  }, [phase, start]);

  const retry = useCallback(() => {
    startedForPhaseRef.current = true;
    start();
  }, [start]);

  if (status === "ready" && run) {
    return (
      <Suspense
        fallback={
          <section
            aria-label="Loading"
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4 py-8 text-sm text-slate-400"
            data-testid="workspace-script-loading"
          >
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading…
          </section>
        }
      >
        {/* AW-2C-005. `customer` is a SIBLING prop, never folded into
            `embedded`. `normalizeEmbeddedRun` treats that object as the
            all-or-nothing run-identity contract -- scriptId and runId,
            both or neither -- and putting customer data inside it would
            mean the authority contract and a mutable per-call snapshot
            shared one shape. The snapshot changes mid-call; the run
            identity must not. */}
        <ScriptPlayPage
          embedded={{ scriptId: run.scriptId, runId: run.runId }}
          customer={customer}
        />
      </Suspense>
    );
  }

  if (status === "starting") {
    return (
      <section
        aria-label="Loading"
        className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4 py-8 text-sm text-slate-400"
        data-testid="workspace-script-loading"
      >
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading…
      </section>
    );
  }

  if (status === "error") {
    return (
      <section
        aria-label="Unavailable"
        className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4 py-8 text-center"
        data-testid="workspace-script-unavailable"
        role="alert"
      >
        <p className="text-sm text-slate-300">
          Nothing is available for this call.
        </p>
        <button
          type="button"
          onClick={retry}
          className="rounded-md border border-slate-600 px-3 py-1 text-xs text-slate-300"
          data-testid="workspace-script-retry"
        >
          Try again
        </button>
      </section>
    );
  }

  return (
    <section
      aria-label="Workspace panels"
      className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 px-4 py-8 lg:py-16 lg:overflow-y-auto"
      data-testid="workspace-panel-region"
    >
      <p className="text-sm text-slate-500" data-testid="workspace-panel-region-empty">
        Nothing to show until a call connects.
      </p>
    </section>
  );
}
